import React, { useCallback, useEffect, useMemo, useState } from "react";
import Navbar from "../Navbar/Navbar";
import Bredcom from "../Bredcom/Bredcom";
import Footer from "../Footer/Footer";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  fetchCmsErasmusCallBySlug,
  fetchCmsErasmusCalls,
  normalizeCmsErasmusCallForLegacyView,
  normalizeCmsErasmusCallsForLegacyView,
  resolveCmsHtmlAssetUrls,
} from "../../api/cmsClient";
import { getCmsLocaleFromLanguage, hasEnglishCmsVersion } from "../../api/cmsLocale";
import {
  cacheCmsErasmusCalls,
  cacheCmsErasmusDetail,
  createErasmusFallbackNotice,
  getCachedCmsErasmusCalls,
  getCachedCmsErasmusDetail,
  shouldSuppressErasmusFallbackNotice,
} from "../../api/erasmusFallback";
import ErasmusFallbackNotice from "./ErasmusFallbackNotice";
import SEO from "../../seo/SEO";
import { buildSectionSeoTitle } from "../../seo/seoText";
import { buildLegacyErasmusKonkursi } from "../../data/erasmusKonkursiLegacy";
import {
  mergeErasmusCallWithLocalLogoFallback,
  mergeErasmusCallsWithLocalLogoFallbacks,
} from "../../api/legacyMediaMerge";


function compactText(value = "", maxLength = 165) {
  const normalized = String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trim()}…`;
}

function getErasmusSeoDescription(konkurs, isEnglish) {
  if (!konkurs) {
    return isEnglish
      ? "Current Erasmus calls at FPEP Bar: student, teaching and staff mobilities, application requirements and supporting documents."
      : "Aktuelni Erasmus konkursi na FPEP Bar: mobilnosti za studente, nastavno i nenastavno osoblje, uslovi prijave i prateća dokumentacija.";
  }

  return compactText(
    konkurs.seoDescription || konkurs.opis || konkurs.tekst || konkurs.contentHtml,
    170,
  );
}

function getErasmusSeoPath(konkurs) {
  if (!konkurs?.slug) return "/erasmus-konkursi";
  return `/erasmus-konkursi?konkurs=${encodeURIComponent(konkurs.slug)}`;
}

function normalizeLogoCandidates(konkurs) {
  const candidates = Array.isArray(konkurs?.logoCandidates) && konkurs.logoCandidates.length
    ? konkurs.logoCandidates
    : [konkurs?.logo];

  return [...candidates, "/assets/img/erasmuskonkursi/adriatiklogo.png"].filter(Boolean);
}

function InstitutionLogo({ konkurs }) {
  const candidates = normalizeLogoCandidates(konkurs);
  const candidatesKey = candidates.join("|");
  const [candidateIndex, setCandidateIndex] = useState(0);

  useEffect(() => {
    setCandidateIndex(0);
  }, [candidatesKey]);

  if (!candidates.length) return null;

  return (
    <img
      src={candidates[candidateIndex]}
      alt={konkurs.ustanova}
      className="ustanova-logo"
      loading="lazy"
      decoding="async"
      onError={(event) => {
        const nextIndex = candidateIndex + 1;
        if (nextIndex < candidates.length) {
          event.currentTarget.src = candidates[nextIndex];
          setCandidateIndex(nextIndex);
        }
      }}
    />
  );
}

const ErasmusKonkurs = () => {
  const [t, i18n] = useTranslation();
  const cmsLocale = getCmsLocaleFromLanguage(i18n.language);
  const isEnglish = cmsLocale === "en";
  const [activeIndex, setActiveIndex] = useState(null); // Drži podatak o aktivnom konkursu
  const [konkursi, setKonkursi] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fallbackNotice, setFallbackNotice] = useState(null);
  const [fallbackSource, setFallbackSource] = useState(null);
  const [retryKey, setRetryKey] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const selectedKonkursSlug = useMemo(
    () => new URLSearchParams(location.search).get("konkurs") || "",
    [location.search],
  );

  const localKonkursi = useMemo(() => buildLegacyErasmusKonkursi(i18n.language), [i18n.language]);

  const loadCmsErasmusCalls = useCallback(async () => {
    setIsLoading(true);

    try {
      const response = await fetchCmsErasmusCalls({ page: 1, limit: 80, locale: cmsLocale });
      const responseData = response?.data;
      const rawCmsCalls = Array.isArray(responseData)
        ? responseData
        : Array.isArray(responseData?.items)
          ? responseData.items
          : [];
      const cmsCalls = mergeErasmusCallsWithLocalLogoFallbacks(
        normalizeCmsErasmusCallsForLegacyView(rawCmsCalls),
        localKonkursi,
      );

      if (cmsCalls.length > 0) {
        cacheCmsErasmusCalls(cmsCalls, cmsLocale);
        setKonkursi(cmsCalls);
        setFallbackNotice(null);
        setFallbackSource(null);
        return;
      }

      const cachedCalls = getCachedCmsErasmusCalls(cmsLocale);
      if (cachedCalls.length > 0) {
        setKonkursi(cachedCalls);
        setFallbackSource("cache");
        setFallbackNotice(createErasmusFallbackNotice({ source: "cache", reason: "empty" }));
        return;
      }

      setKonkursi(localKonkursi);
      setFallbackSource("local");
      setFallbackNotice(createErasmusFallbackNotice({ source: "local", reason: "empty" }));
    } catch (error) {
      const cachedCalls = getCachedCmsErasmusCalls(cmsLocale);

      if (cachedCalls.length > 0) {
        setKonkursi(cachedCalls);
        setFallbackSource("cache");
        setFallbackNotice(
          shouldSuppressErasmusFallbackNotice(error)
            ? null
            : createErasmusFallbackNotice({ source: "cache", reason: "unavailable" }),
        );
        return;
      }

      setKonkursi(localKonkursi);
      setFallbackSource("local");
      setFallbackNotice(
        shouldSuppressErasmusFallbackNotice(error)
          ? null
          : createErasmusFallbackNotice({ source: "local", reason: "unavailable" }),
      );
    } finally {
      setIsLoading(false);
    }
  }, [localKonkursi, cmsLocale]);

  useEffect(() => {
    loadCmsErasmusCalls();
  }, [loadCmsErasmusCalls, retryKey]);

  const hydrateCmsKonkurs = useCallback(async (index) => {
    const konkurs = konkursi[index];

    if (!konkurs || konkurs.source !== "cms" || !konkurs.slug || konkurs.detailsLoaded) {
      return;
    }

    const cachedDetail = getCachedCmsErasmusDetail(konkurs.slug, cmsLocale);
    if (cachedDetail) {
      setKonkursi((current) =>
        current.map((item, itemIndex) => (itemIndex === index ? { ...item, ...cachedDetail } : item)),
      );
      return;
    }

    try {
      const response = await fetchCmsErasmusCallBySlug(konkurs.slug, { locale: cmsLocale });
      const normalized = mergeErasmusCallWithLocalLogoFallback(
        normalizeCmsErasmusCallForLegacyView(response?.data),
        localKonkursi,
      );

      if (!normalized) return;

      cacheCmsErasmusDetail(konkurs.slug, normalized, cmsLocale);
      setKonkursi((current) =>
        current.map((item, itemIndex) => (itemIndex === index ? { ...item, ...normalized } : item)),
      );
    } catch (_error) {
      // A failed detail hydrate must not close the accordion or break the page.
    }
  }, [konkursi, localKonkursi, cmsLocale]);

  const updateKonkursQuery = useCallback((konkurs) => {
    const params = new URLSearchParams(location.search);

    if (konkurs?.slug) {
      params.set("konkurs", konkurs.slug);
    } else {
      params.delete("konkurs");
    }

    const query = params.toString();
    navigate(`${location.pathname}${query ? `?${query}` : ""}${location.hash || ""}`, {
      replace: false,
    });
  }, [location.hash, location.pathname, location.search, navigate]);

  const handleToggle = useCallback((index) => {
    setActiveIndex((current) => {
      const nextIndex = current === index ? null : index;

      if (nextIndex !== null) {
        hydrateCmsKonkurs(index);
        updateKonkursQuery(konkursi[index]);
      } else {
        updateKonkursQuery(null);
      }

      return nextIndex;
    });
  }, [hydrateCmsKonkurs, konkursi, updateKonkursQuery]);

  useEffect(() => {
    if (!selectedKonkursSlug) {
      setActiveIndex(null);
      return;
    }

    if (konkursi.length === 0) return;

    const index = konkursi.findIndex((konkurs) => konkurs.slug === selectedKonkursSlug);
    if (index === -1) {
      setActiveIndex(null);
      return;
    }

    setActiveIndex(index);
    hydrateCmsKonkurs(index);
  }, [selectedKonkursSlug, konkursi, hydrateCmsKonkurs]);

  const renderAdditionalText = (konkurs) => {
    if (konkurs.contentHtml) {
      return (
        <div
          className="additional-text mt-3 cms-rich-content"
          dangerouslySetInnerHTML={{ __html: resolveCmsHtmlAssetUrls(konkurs.contentHtml) }}
        />
      );
    }

    if (!konkurs.tekst) return null;

    return (
      <div className="additional-text mt-3">
        {String(konkurs.tekst)
          .split("\n")
          .filter(Boolean)
          .map((line, i) => (
            <p key={i}>{line}</p>
          ))}
      </div>
    );
  };

  const handleRetry = () => {
    setRetryKey((value) => value + 1);
  };

  const activeKonkurs = activeIndex !== null ? konkursi[activeIndex] : null;
  const isMissingSelectedKonkurs = Boolean(
    selectedKonkursSlug
      && !isLoading
      && !activeKonkurs
      && !konkursi.some((konkurs) => konkurs.slug === selectedKonkursSlug),
  );
  const seoTitle = isMissingSelectedKonkurs
    ? isEnglish
      ? "Erasmus call not found | FPEP Bar"
      : "Erasmus konkurs nije pronađen | FPEP Bar"
    : buildSectionSeoTitle(
        activeKonkurs?.seoTitle || activeKonkurs?.naslov || (isEnglish ? "Erasmus Calls" : "Erasmus konkursi"),
        activeKonkurs ? (isEnglish ? "Erasmus Calls" : "Erasmus konkursi") : "",
      );
  const seoDescription = isMissingSelectedKonkurs
    ? isEnglish
      ? "The requested FPEP Bar Erasmus call is unavailable or no longer published."
      : "Traženi Erasmus konkurs FPEP Bar nije dostupan ili više nije objavljen."
    : getErasmusSeoDescription(activeKonkurs, isEnglish);
  const seoPath = activeKonkurs
    ? getErasmusSeoPath(activeKonkurs)
    : selectedKonkursSlug
      ? `/erasmus-konkursi?konkurs=${encodeURIComponent(selectedKonkursSlug)}`
      : "/erasmus-konkursi";
  const seoImage = activeKonkurs?.ogImage
    || activeKonkurs?.logo
    || "/assets/img/social/fpep-international.webp";
  const activeKonkursHasEnglishVersion = activeKonkurs ? hasEnglishCmsVersion(activeKonkurs) : true;
  const isEnglishFallbackDetail = Boolean(
    activeKonkurs && isEnglish && !activeKonkursHasEnglishVersion,
  );

  return (
    <>
      {(!selectedKonkursSlug || !isLoading || konkursi.length > 0) && (
        <SEO
          title={seoTitle}
          description={seoDescription}
          pathname={seoPath}
          pageType={activeKonkurs ? "Article" : isMissingSelectedKonkurs ? "WebPage" : "CollectionPage"}
          image={seoImage}
          imageAlt={activeKonkurs?.naslov || seoTitle}
          imageType={!activeKonkurs ? "image/webp" : undefined}
          imageWidth={!activeKonkurs ? 1200 : undefined}
          imageHeight={!activeKonkurs ? 630 : undefined}
          section={isEnglish ? "Erasmus Calls" : "Erasmus konkursi"}
          noindex={isEnglishFallbackDetail || isMissingSelectedKonkurs}
          canonicalLocale={isEnglishFallbackDetail ? "me" : undefined}
          englishAlternateAvailable={!isMissingSelectedKonkurs && activeKonkursHasEnglishVersion}
          disableLanguageAlternates={isMissingSelectedKonkurs}
          article={activeKonkurs ? {
            headline: activeKonkurs.naslov,
            datePublished: activeKonkurs.publishedAt,
            dateModified: activeKonkurs.updatedAt || activeKonkurs.publishedAt,
            image: seoImage,
            section: isEnglish ? "Erasmus Calls" : "Erasmus konkursi",
          } : null}
          breadcrumbs={[
            { name: isEnglish ? "Home" : "Početna", path: "/" },
            {
              name: isEnglish ? "International Cooperation" : "Međunarodna saradnja",
              path: "/medjunarodna-saradnja",
            },
            { name: isEnglish ? "Erasmus Calls" : "Erasmus konkursi", path: "/erasmus-konkursi" },
            ...(activeKonkurs ? [{ name: activeKonkurs.naslov, path: seoPath }] : []),
          ]}
        />
      )}
      <Navbar />
      <Bredcom
        title={t("erasmusKonkurs.pageTitle")}
        subtitle={t("erasmusKonkurs.subtitle")}
      />
      <div className="container mt-4 pt-40">
        <div className="row">
          <div className="col-md-8">
            <div className="content-container">
              <h2 className="section-title">
                {t("erasmusKonkurs.competitionList.title")}
              </h2>
              <div className="logo-container text-center mb-4">
                <img
                  src="/assets/img/erasmuskonkursi/adriatiklogo.png"
                  alt="Univerzitet Adriatik"
                  className="logo-img"
                />
                <img
                  src="/assets/img/erasmuskonkursi/sufinansiraEU.png"
                  alt="Sufinansira Evropska Unija"
                  className="logo-img"
                />
              </div>
              <div className="ofpep-section">
                <p className="gradient-border-text text-center ofpep-section">
                  {t("erasmusKonkurs.competitionList.text")}
                </p>
                <h4 className="text-center">
                  {t("erasmusKonkurs.competitionList.region")}
                </h4>
              </div>
              <ErasmusFallbackNotice
                message={fallbackNotice}
                source={fallbackSource}
                onRetry={handleRetry}
                retrying={isLoading}
              />

              {isLoading && konkursi.length === 0 && (
                <div className="cms-fallback-notice fpep-erasmus-loading" role="status">
                  <div className="cms-fallback-notice__content">
                    <span className="cms-fallback-notice__badge">CMS</span>
                    <span>{isEnglish ? "Loading Erasmus calls..." : "Učitavanje Erasmus konkursa..."}</span>
                  </div>
                </div>
              )}

              <div className="konkurs-list pt-40 pb-40">
                {konkursi.map((konkurs, index) => (
                  <div key={konkurs.cmsId || konkurs.id || index} className="konkurs-item">
                    <div className="meta-info mb-2">
                      <i className="fas fa-calendar-alt"></i> {konkurs.datum}
                    </div>
                    <h3>{konkurs.naslov}</h3>
                    <p>{konkurs.opis}</p>
                    <div className="konkurs-ustanova">
                      <InstitutionLogo konkurs={konkurs} />
                      <span>{konkurs.ustanova}</span>
                    </div>
                    {(konkurs.hasMoreInfo || konkurs.dokumenti?.length || konkurs.tekst || konkurs.contentHtml) && (
                      <>
                        <button
                          className={`faq-btn${
                            activeIndex === index ? "" : " collapsed"
                          }`}
                          type="button"
                          onClick={() => handleToggle(index)}
                        >
                          {activeIndex === index
                            ? t(
                                "erasmusKonkurs.competitionList.closeInfoButton",
                              )
                            : t(
                                "erasmusKonkurs.competitionList.moreInfoButton",
                              )}
                        </button>
                        <div
                          className={`collapse${
                            activeIndex === index ? " show" : ""
                          }`}
                        >
                          <div className="pt-40 textjustify">
                            {konkurs.source === "cms" && activeIndex === index && !konkurs.detailsLoaded && konkurs.documentCount > 0 && (
                              <p className="erasmus-detail-loading">{isEnglish ? "Documents are loading..." : "Dokumenti se učitavaju..."}</p>
                            )}
                            {konkurs.dokumenti?.length > 0 && (
                              <>
                                <h4>
                                  {t(
                                    "erasmusKonkurs.competitionList.newKA171.documents",
                                  )}
                                </h4>
                                <ul>
                                  {konkurs.dokumenti.map((dokument, i) => (
                                    <li key={i}>
                                      <a
                                        href={dokument.url}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <i className="fas fa-external-link-alt"></i>{" "}
                                        <strong>{dokument.naziv}</strong>
                                      </a>
                                    </li>
                                  ))}
                                </ul>
                              </>
                            )}
                            {renderAdditionalText(konkurs)}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar sekcija */}
          <div className="col-md-4 pt-10">
            <aside className="sidebar-widget">
              <section
                id="custom_html-5"
                className="widget_text widget widget_custom_html"
              >
                <h2 className="widget-title">{t("sidebar.follow")}</h2>
                <div className="textwidget custom-html-widget">
                  <div className="widget-social">
                    <a
                      href="https://www.facebook.com/fpebar.me"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Facebook"
                    >
                      <i className="fab fa-facebook-f" />
                    </a>
                    <a
                      href="https://www.instagram.com/fpepbar/"
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Instagram"
                    >
                      <i className="fab fa-instagram" />
                    </a>
                    <a
                      href="https://www.youtube.com/@fpepbar"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <i className="fab fa-youtube" />
                    </a>
                    <a
                      href="https://www.tiktok.com/@fpepbar"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Follow us on Tiktok"
                      title="Tiktok"
                    >
                      <i className="fab fa-tiktok" />
                    </a>
                    <a
                      href="https://www.linkedin.com/school/fpepbar/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Follow us on LinkedIn"
                      title="LinkedIn"
                    >
                      <i className="fab fa-linkedin-in" />
                    </a>
                  </div>
                </div>
              </section>
              <section id="categories-1" className="widget widget_categories">
                <h2 className="widget-title">{t("sidebar.useful")}</h2>
                <ul>
                  <li className="cat-item cat-item-16">
                    <Link to="/o-fpep">{t("about.fpep")}</Link>
                  </li>
                  <li className="cat-item cat-item-23">
                    <Link to="/akreditacija-fpep">
                      {t("admission.Akreditacija")}
                    </Link>
                  </li>
                  <li className="cat-item cat-item-18">
                    <Link to="/upis">{t("upis.title")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/cijene-studija">{t("admission.Skolarina")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/erasmus">{t("saradnja.erasmus")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/projekti">{t("saradnja.projekti")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/dom-studenata">{t("studenti.dom")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/galerija">{t("navbar.Gallery")}</Link>
                  </li>
                  <li className="cat-item cat-item-22">
                    <Link to="/novosti">{t("navbar.News")}</Link>
                  </li>
                </ul>
              </section>
              <section
                id="recent-posts-4"
                className="widget widget_recent_entries"
              >
                <h2 className="widget-title">{t("sidebar.study")}</h2>
                <ul>
                  <li>
                    <span className="post-date">
                      <strong>{t("programs.osnovne")}</strong> <br />
                    </span>
                    <Link to="/racunovodstvo-finansije-i-bankarstvo">
                      {t("osnovne.rfb")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/menadzment">
                      {t("osnovne.menadzment")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/pravo-osnovne">
                      {t("osnovne.pravo")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <span className="post-date">
                      <strong>{t("programs.master")}</strong> <br />
                    </span>
                    <Link to="/poslovna-ekonomija">
                      {t("master.poslovnaEkonomija")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/ekonomija-javnog-sektora">
                      {t("master.ekonomijaJavnogSektora")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/pravo-master">
                      {t("master.pravo")}
                      <br />
                    </Link>
                  </li>

                  <li>
                    <span className="post-date">
                      <strong>{t("programs.specijalisticke")}</strong> <br />
                    </span>
                    <Link to="/menadzment-javnog-sektora-specijalisticke">
                      {t("specijalisticke.mjs")}
                      <br />
                    </Link>
                  </li>
                  <li>
                    <Link to="/menadzment-profitnog-sektora-specijalisticke">
                      {t("specijalisticke.mps")}
                      <br />
                    </Link>
                  </li>
                </ul>
              </section>
            </aside>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
};

export default ErasmusKonkurs;
